import React, { useCallback, useEffect, useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import {
  Box, Button, Chip, CircularProgress, Divider, IconButton,
  Paper, Stack, Tooltip, Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import DownloadIcon from '@mui/icons-material/Download';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import CloudOffIcon from '@mui/icons-material/CloudOff';
import Layout from '../components/Layout';
import ProjectService from '../services/ProjectService';
import PortalAuthService from '../services/PortalAuthService';
import { useToast } from '../contexts/ToastContext';

const fmt = (v) =>
  v ? new Date(v).toLocaleDateString('de-CH', { day: '2-digit', month: '2-digit', year: 'numeric' }) : '—';

const fmtDateTime = (v) =>
  v ? new Date(v).toLocaleString('de-CH', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—';

const SideLabel = ({ children }) => (
  <Typography sx={{ fontSize: 11, color: '#94A3B8', textTransform: 'uppercase', fontWeight: 600, letterSpacing: 0.5, mb: 0.4 }}>
    {children}
  </Typography>
);

const SideValue = ({ children, sx }) => (
  <Typography sx={{ fontSize: 13, fontWeight: 500, color: '#1E293B', ...sx }}>
    {children}
  </Typography>
);

const SectionHeader = ({ title, action }) => (
  <Box sx={{ px: 3, py: 2, borderBottom: '1px solid #F1F5F9', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
    <Typography sx={{ fontSize: 11, fontWeight: 700, color: '#94A3B8', textTransform: 'uppercase', letterSpacing: 1 }}>
      {title}
    </Typography>
    {action}
  </Box>
);

const STATUS_META = {
  DRAFT:                       { label: 'Draft',              color: '#94a3b8' },
  PUBLISHED:                   { label: 'Published',          color: '#64748b' },
  SIGNED:                      { label: 'Signed',             color: '#22c55e' },
  REFUSED:                     { label: 'Refused',            color: '#ef4444' },
  CUSTOMER_NOT_AVAILABLE:      { label: 'Not Available',      color: '#f59e0b' },
  AWAITING_REMOTE_SIGNATURE:   { label: 'Awaiting Signature', color: '#3b82f6' },
  REMOTE_SIGNATURE_EXPIRED:    { label: 'Expired',            color: '#f97316' },
  CONFIRMED_WITHOUT_SIGNATURE: { label: 'Confirmed',          color: '#64748b' },
};

const PORTAL_ELIGIBLE = ['SIGNED', 'CONFIRMED_WITHOUT_SIGNATURE'];

export default function ReportInfo() {
  const { id: reportId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { showToast } = useToast();

  const stateReport = location.state?.report || null;
  const projectId = location.state?.projectId || null;

  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [pdfLoading, setPdfLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);
  const [customerPortalActive, setCustomerPortalActive] = useState(false);
  const [portalLoading, setPortalLoading] = useState(false);

  const reloadReport = useCallback(() => {
    ProjectService.getReport(reportId)
      .then(data => setReport(data))
      .catch(console.error);
  }, [reportId]);

  useEffect(() => {
    ProjectService.getReport(reportId)
      .then(data => { setReport(data); setLoading(false); })
      .catch(() => setLoading(false));
  }, [reportId]);

  useEffect(() => {
    if (!report?.projectId) return;
    // Need the customer ID — fetch project to get it
    // The getReport DTO doesn't carry customerId, but we can check via a lightweight call
    // We check portal status based on project's customer
    // For now, attempt via the project info endpoint
    ProjectService.getProjectInfo(report.projectId)
      .then(() => {
        // We have project info but it doesn't include customer ID directly.
        // Use a different approach: fetch from /api/projects/{id} — but that needs pageable.
        // Instead, skip portal check until a better API exists; leave portal inactive.
      })
      .catch(() => {});
  }, [report?.projectId]);

  // Fetch portal status using projectId from state (passed from ProjectInfo)
  useEffect(() => {
    if (!stateReport?.project?.customer?.id && !projectId) return;
    // stateReport from the row only has projectId as an int, not a nested object
    // We rely on the parent passing projectId in location.state, then fetching
    // project's customer from the report DTO customerName (no ID). We skip portal.
    // If the parent's ProjectInfo already checks portal, we piggyback on that via
    // a quick fetch of the project:
  }, [projectId, stateReport]);

  // Load PDF
  useEffect(() => {
    let url;
    setPdfLoading(true);
    ProjectService.downloadPdfReport(reportId)
      .then(data => {
        url = URL.createObjectURL(new Blob([data.data], { type: 'application/pdf' }));
        setPdfUrl(url);
      })
      .catch(console.error)
      .finally(() => setPdfLoading(false));
    return () => { if (url) URL.revokeObjectURL(url); };
  }, [reportId]);

  // Check portal status via project
  useEffect(() => {
    if (!report?.projectId) return;
    // Fetch the full project to get the customer ID for portal check
    ProjectService.getProject(0, 1, report.projectId)
      .then(data => {
        const customerId = data?.project?.customer?.id;
        if (!customerId) return;
        return PortalAuthService.getPortalStatus(customerId);
      })
      .then(data => { if (data) setCustomerPortalActive(data?.portalStatus === 'ACTIVE'); })
      .catch(() => {});
  }, [report?.projectId]);

  const handleDownload = async () => {
    setDownloading(true);
    try {
      const data = await ProjectService.downloadPdfReport(reportId);
      const cd = data.headers?.['content-disposition'] || '';
      const match = cd.match(/filename\*?=(?:UTF-8'')?"?([^";]+)/i);
      const name = match ? decodeURIComponent(match[1]) : `report-${reportId}.pdf`;
      const blobUrl = URL.createObjectURL(new Blob([data.data]));
      const a = document.createElement('a');
      a.href = blobUrl; a.download = name;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(blobUrl);
    } catch { console.error('Failed to download report'); }
    finally { setDownloading(false); }
  };

  const handlePublishToPortal = async () => {
    setPortalLoading(true);
    try {
      const updated = await ProjectService.publishReportToPortal(reportId);
      setReport(prev => ({ ...prev, publishedToPortal: updated.publishedToPortal, publishedToPortalAt: updated.publishedToPortalAt }));
      showToast('Report sent to portal', 'success');
    } catch { showToast('Failed to send to portal', 'error'); }
    finally { setPortalLoading(false); }
  };

  const handleUnpublishFromPortal = async () => {
    setPortalLoading(true);
    try {
      const updated = await ProjectService.unpublishReportFromPortal(reportId);
      setReport(prev => ({ ...prev, publishedToPortal: updated.publishedToPortal, publishedToPortalAt: null }));
      showToast('Report removed from portal', 'info');
    } catch { showToast('Failed to remove from portal', 'error'); }
    finally { setPortalLoading(false); }
  };

  if (loading) {
    return (
      <Layout>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
          <CircularProgress sx={{ color: '#427898' }} />
        </Box>
      </Layout>
    );
  }

  const sm = STATUS_META[report?.status] || { label: report?.status || '—', color: '#94a3b8' };
  const isFieldService = report?.type === 'FIELD_SERVICE';
  const canPublishToPortal = PORTAL_ELIGIBLE.includes(report?.status);

  return (
    <Layout fullBleed>
      <Box sx={{ bgcolor: '#F8FAFC', minHeight: '100vh' }}>

        {/* ── Sticky header ── */}
        <Box sx={{
          bgcolor: '#fff', borderBottom: '1px solid #EEF2F6',
          px: 3, py: 1.5, display: 'flex', alignItems: 'center', gap: 2,
          position: 'sticky', top: 0, zIndex: 10,
        }}>
          <Button
            size="small"
            startIcon={<ArrowBackIcon sx={{ fontSize: '16px !important' }} />}
            onClick={() => projectId ? navigate(`/project-info/${projectId}`) : navigate(-1)}
            sx={{ color: '#64748B', fontWeight: 500, textTransform: 'none', fontSize: 13, px: 1, minWidth: 0, '&:hover': { bgcolor: '#F1F5F9' } }}
          >
            Project
          </Button>

          <Divider orientation="vertical" flexItem sx={{ height: 16, alignSelf: 'center' }} />

          <Typography sx={{ fontWeight: 700, fontSize: 15, color: '#0F172A' }}>
            {report?.displayName || report?.name || `Report #${reportId}`}
          </Typography>

          <Chip
            label={sm.label}
            size="small"
            sx={{ bgcolor: sm.color + '18', color: sm.color, fontWeight: 600, fontSize: 11, border: `1px solid ${sm.color}30`, height: 22 }}
          />

          <Chip
            label={isFieldService ? 'Field Service' : 'Internal'}
            size="small"
            sx={{ bgcolor: isFieldService ? '#42789818' : '#F1F5F9', color: isFieldService ? '#427898' : '#64748B', fontWeight: 600, fontSize: 11, height: 22 }}
          />

          <Box sx={{ flex: 1 }} />

          <Stack direction="row" spacing={0.5} alignItems="center">
            {customerPortalActive && canPublishToPortal && (
              report?.publishedToPortal ? (
                <Button
                  size="small" variant="outlined" onClick={handleUnpublishFromPortal} disabled={portalLoading}
                  startIcon={<CloudOffIcon sx={{ fontSize: '14px !important' }} />}
                  sx={{ textTransform: 'none', fontSize: 12, fontWeight: 600, borderColor: '#1565C0', color: '#1565C0' }}
                >
                  Unpublish from Portal
                </Button>
              ) : (
                <Button
                  size="small" variant="outlined" onClick={handlePublishToPortal} disabled={portalLoading}
                  startIcon={<CloudUploadIcon sx={{ fontSize: '14px !important' }} />}
                  sx={{ textTransform: 'none', fontSize: 12, fontWeight: 600, borderColor: '#1565C0', color: '#1565C0' }}
                >
                  Send to Portal
                </Button>
              )
            )}
            <Tooltip title="Download PDF">
              <span>
                <IconButton size="small" onClick={handleDownload} disabled={downloading}
                  sx={{ color: '#64748B', '&:hover': { bgcolor: '#F1F5F9' } }}>
                  {downloading
                    ? <CircularProgress size={16} sx={{ color: '#427898' }} />
                    : <DownloadIcon sx={{ fontSize: 18 }} />}
                </IconButton>
              </span>
            </Tooltip>
          </Stack>
        </Box>

        {/* ── Body ── */}
        <Box sx={{
          display: 'flex', gap: 3, p: { xs: 2, md: 3 },
          maxWidth: 1280, mx: 'auto', alignItems: 'flex-start',
          flexDirection: { xs: 'column', md: 'row' },
        }}>

          {/* ── Main column ── */}
          <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 2.5 }}>

            {/* Details card */}
            <Paper elevation={0} sx={{ border: '1px solid #EEF2F6', borderRadius: 2, bgcolor: '#fff', overflow: 'hidden' }}>
              <SectionHeader title="Details" />
              <Box sx={{ px: 3, py: 1 }}>
                {[
                  ['Report ID', report?.displayName || report?.name],
                  ['Report Date', fmt(report?.reportDate)],
                  ['Created', fmt(report?.createdAt)],
                  ['Signer', report?.signerName],
                  ['Signed At', report?.signedAt ? fmtDateTime(report.signedAt) : null],
                  ['Signature Method', report?.signatureMethod],
                  ['Customer Remarks', report?.customerRemarks],
                  ['Refusal Reason', report?.refusalReason],
                ].filter(([, v]) => v != null).map(([label, value]) => (
                  <Box key={label} sx={{ py: 1.25, borderBottom: '1px solid #F1F5F9', display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2 }}>
                    <Typography sx={{ fontSize: 12, color: '#64748B', fontWeight: 500, flexShrink: 0 }}>{label}</Typography>
                    <Typography sx={{ fontSize: 13, fontWeight: 600, color: '#1E293B', textAlign: 'right' }}>{value}</Typography>
                  </Box>
                ))}
                {report?.publishedToPortal && (
                  <Box sx={{ py: 1.25, borderBottom: '1px solid #F1F5F9', display: 'flex', justifyContent: 'space-between', gap: 2 }}>
                    <Typography sx={{ fontSize: 12, color: '#64748B', fontWeight: 500 }}>Portal</Typography>
                    <Typography sx={{ fontSize: 13, fontWeight: 600, color: '#1565C0' }}>
                      Sent {report.publishedToPortalAt ? fmtDateTime(report.publishedToPortalAt) : ''}
                    </Typography>
                  </Box>
                )}
              </Box>
              {report?.workDescription && (
                <>
                  <Divider />
                  <Box sx={{ px: 3, py: 2 }}>
                    <Typography sx={{ fontSize: 11, fontWeight: 700, color: '#94A3B8', textTransform: 'uppercase', letterSpacing: 1, mb: 1 }}>
                      Work Description
                    </Typography>
                    <Typography sx={{ fontSize: 13, color: '#1E293B', whiteSpace: 'pre-wrap', lineHeight: 1.6 }}>
                      {report.workDescription}
                    </Typography>
                  </Box>
                </>
              )}
            </Paper>

            {/* PDF document card */}
            <Paper elevation={0} sx={{ border: '1px solid #EEF2F6', borderRadius: 2, bgcolor: '#fff', overflow: 'hidden' }}>
              <SectionHeader
                title="Document"
                action={
                  <Button size="small" startIcon={<DownloadIcon sx={{ fontSize: '14px !important' }} />}
                    onClick={handleDownload} disabled={downloading}
                    sx={{ textTransform: 'none', fontWeight: 600, fontSize: 12, color: '#427898', px: 1.5, '&:hover': { bgcolor: '#EFF6FF' } }}>
                    Download
                  </Button>
                }
              />
              <Box sx={{ height: 860, position: 'relative', bgcolor: '#F8FAFC' }}>
                {pdfLoading ? (
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', gap: 2 }}>
                    <CircularProgress size={28} sx={{ color: '#427898' }} />
                    <Typography sx={{ fontSize: 13, color: '#94A3B8' }}>Loading document…</Typography>
                  </Box>
                ) : pdfUrl ? (
                  <iframe
                    src={pdfUrl}
                    title="Report PDF"
                    style={{ width: '100%', height: '100%', border: 'none', display: 'block' }}
                  />
                ) : (
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                    <Typography sx={{ fontSize: 13, color: '#94A3B8' }}>Document could not be loaded.</Typography>
                  </Box>
                )}
              </Box>
            </Paper>
          </Box>

          {/* ── Sidebar ── */}
          <Box sx={{ width: { md: 288 }, flexShrink: 0, display: 'flex', flexDirection: 'column', gap: 2, position: { md: 'sticky' }, top: { md: 72 } }}>

            {/* Project info card */}
            <Paper elevation={0} sx={{ border: '1px solid #EEF2F6', borderRadius: 2, bgcolor: '#fff', p: 2.5 }}>
              <Typography sx={{ fontSize: 11, fontWeight: 700, color: '#94A3B8', textTransform: 'uppercase', letterSpacing: 1, mb: 2 }}>
                Project
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                <Box>
                  <SideLabel>Project</SideLabel>
                  <SideValue>
                    {projectId ? (
                      <Box
                        component="span"
                        onClick={() => navigate(`/project-info/${projectId}`)}
                        sx={{ cursor: 'pointer', color: '#427898', '&:hover': { textDecoration: 'underline' } }}
                      >
                        {report?.projectName || `Project #${report?.projectId}`}
                      </Box>
                    ) : (report?.projectName || '—')}
                  </SideValue>
                </Box>
                <Box>
                  <SideLabel>Customer</SideLabel>
                  <SideValue>{report?.customerName || '—'}</SideValue>
                </Box>
                <Divider sx={{ my: 0.5 }} />
                <Box>
                  <SideLabel>Status</SideLabel>
                  <Box sx={{ mt: 0.5 }}>
                    <Chip
                      label={sm.label}
                      size="small"
                      sx={{ bgcolor: sm.color + '18', color: sm.color, fontWeight: 600, fontSize: 11, border: `1px solid ${sm.color}30`, height: 22 }}
                    />
                  </Box>
                </Box>
                <Box>
                  <SideLabel>Type</SideLabel>
                  <Box sx={{ mt: 0.5 }}>
                    <Chip
                      label={isFieldService ? 'Field Service' : 'Internal'}
                      size="small"
                      sx={{ bgcolor: isFieldService ? '#42789818' : '#F1F5F9', color: isFieldService ? '#427898' : '#64748B', fontWeight: 600, fontSize: 11, height: 22 }}
                    />
                  </Box>
                </Box>
                {report?.reportDate && (
                  <Box>
                    <SideLabel>Report Date</SideLabel>
                    <SideValue>{fmt(report.reportDate)}</SideValue>
                  </Box>
                )}
                {report?.publishedToPortal && (
                  <Box sx={{ mt: 0.5, p: 1.5, bgcolor: '#EFF6FF', borderRadius: 1.5, border: '1px solid #BFDBFE' }}>
                    <Typography sx={{ fontSize: 11, fontWeight: 700, color: '#1565C0', textTransform: 'uppercase', letterSpacing: 0.5, mb: 0.5 }}>
                      Published to Portal
                    </Typography>
                    {report.publishedToPortalAt && (
                      <Typography sx={{ fontSize: 12, color: '#1E40AF' }}>
                        {fmtDateTime(report.publishedToPortalAt)}
                      </Typography>
                    )}
                  </Box>
                )}
              </Box>
            </Paper>

            {/* Signature card — only when signed */}
            {(report?.signerName || report?.signedAt) && (
              <Paper elevation={0} sx={{ border: '1px solid #EEF2F6', borderRadius: 2, bgcolor: '#fff', p: 2.5 }}>
                <Typography sx={{ fontSize: 11, fontWeight: 700, color: '#94A3B8', textTransform: 'uppercase', letterSpacing: 1, mb: 2 }}>
                  Signature
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                  {report?.signerName && (
                    <Box>
                      <SideLabel>Signed by</SideLabel>
                      <SideValue>{report.signerName}</SideValue>
                    </Box>
                  )}
                  {report?.signedAt && (
                    <Box>
                      <SideLabel>Signed at</SideLabel>
                      <SideValue>{fmtDateTime(report.signedAt)}</SideValue>
                    </Box>
                  )}
                  {report?.signatureMethod && (
                    <Box>
                      <SideLabel>Method</SideLabel>
                      <SideValue>{report.signatureMethod}</SideValue>
                    </Box>
                  )}
                  {report?.customerRemarks && (
                    <Box>
                      <SideLabel>Customer Remarks</SideLabel>
                      <SideValue sx={{ fontStyle: 'italic', color: '#64748B' }}>{report.customerRemarks}</SideValue>
                    </Box>
                  )}
                </Box>
              </Paper>
            )}

          </Box>
        </Box>
      </Box>
    </Layout>
  );
}
